import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Input } from '@/components/ui';
import type { RootState } from '@/store';
import type { ResourceTeamAssignment } from '@/types';

interface AssignmentFormProps {
  initialData?: ResourceTeamAssignment | null;
  onSubmit: (data: Partial<ResourceTeamAssignment>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const AssignmentForm: React.FC<AssignmentFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const resources = useSelector((state: RootState) => state.resources.entities) || [];
  const teams = useSelector((state: RootState) => state.teams.teams) || [];

  const [formData, setFormData] = useState({
    resourceId: '',
    teamId: '',
    startDate: new Date().toISOString().split('T')[0],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (initialData) {
      setFormData({
        resourceId: initialData.resourceId,
        teamId: initialData.teamId,
        startDate: initialData.startDate.split('T')[0],
      });
    }
  }, [initialData]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.resourceId) {
      newErrors.resourceId = 'Resource is required';
    }

    if (!formData.teamId) {
      newErrors.teamId = 'Team is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({
        resourceId: formData.resourceId,
        teamId: formData.teamId,
        startDate: new Date(formData.startDate).toISOString(),
      });
    }
  };

  const activeResources = resources.filter(r => r.isActive);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Resource <span className="text-status-error">*</span>
        </label>
        <select
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.resourceId ? 'border-status-error' : 'border-border'
          }`}
          value={formData.resourceId}
          onChange={(e) => handleInputChange('resourceId', e.target.value)}
          disabled={!!initialData}
        >
          <option value="">Select a resource...</option>
          {activeResources.map((resource) => (
            <option key={resource.id} value={resource.id}>
              {resource.firstName} {resource.lastName} ({resource.resourceId}) - {resource.resourceLevel?.code || 'N/A'}
            </option>
          ))}
        </select>
        {errors.resourceId && (
          <p className="mt-1 text-sm text-status-error">{errors.resourceId}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">
          Team <span className="text-status-error">*</span>
        </label>
        <select
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            errors.teamId ? 'border-status-error' : 'border-border'
          }`}
          value={formData.teamId}
          onChange={(e) => handleInputChange('teamId', e.target.value)}
        >
          <option value="">Select a team...</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name} {team.program ? `(${team.program.name})` : ''}
            </option>
          ))}
        </select>
        {errors.teamId && (
          <p className="mt-1 text-sm text-status-error">{errors.teamId}</p>
        )}
      </div>

      <Input
        type="date"
        label="Start Date"
        value={formData.startDate}
        onChange={(e) => handleInputChange('startDate', e.target.value)}
        error={errors.startDate}
        required
      />

      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> This will create a new assignment. The resource will be added to the selected team starting from the specified date.
        </p>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isLoading}>
          {isLoading ? 'Assigning...' : 'Assign Resource'}
        </Button>
      </div>
    </form>
  );
};

export default AssignmentForm;
